import { BaseTranslationService } from '../../abstract-classes/base-translation';
import { Category } from '../category';
import { Job, JobConfiguration, JobStatus } from '../job';
import { LayerType } from '../layer';
import { Pipeline } from '../pipeline';
import { ProjectType } from '../project';
import { PcvizDBRegistry } from './db';
import { PcvizProject, PcvizScanProcessingOptions, PcvizWorkspaceScan, ScanControlPointType } from './gis';
import { PcvizProcess } from './workflow/workflow';

export enum Process {
    NONE = 0,
    POINTCLOUD_PROCESS = 1,
    TILE_IMAGES = 2,
    VECTOR_PROCESS = 3,
    ZIP_PROCESS = 4,
    CONTEXTCAPTURE_PREPARE_AND_UPLOAD = 5,
    CONTEXTCAPTURE_DOWNLOAD_DATA = 6,
    CONTEXTCAPTURE_DO_PROCESSING = 7,
    CREATE_PROJECT = 8,
    GIS_PROCESS = 9,
    OBJ_PROCESS = 10,
    C3DTILES_PROCESS = 11,
    DOWNLOAD_DATA = 12,
    IMPORT_SCAN_PROJECT = 13,
    SCAN_PROCESSING = 14,
    DELETE_DATA = 16,
    C2C_REGISTRATION = 17,
    OCCBS_REGISTRATION = 18,
    RESECTION_REGISTRATION = 19,
    RASTER = 20,
    EXPORT_SCANS = 21,
    CE_OCCBS_REGISTRATION = 22,
    CE_RESECTION_REGISTRATION = 23,
    SYNC_PROJECT = 24,
    UPDATE_SPARSE = 25,
    EXPORT = 26,
    IMPORT = 27,
    SCAN_COMMON_PROCESS = 28,
    MW_FILTERS_COMMON_PROCESS = 30,
    SCAN_DELIVERABLES = 31,
    BIM_PROCESS = 32,
    VERITY_ANALYSIS = 33,
    VOLUME_CALCULATION = 34,
    GENERATE_SNAPSHOT = 35,
    IMPORT_REFERENCES_TO_SITES = 38,
    EXPORT_PANORAMA = 39,
    PROCESS_FLS_SCAN = 40,
    PROCESS_SCAN_V2 = 41,
    IMPORT_MULTIPLE_STATION_V2 = 43,
    PUBLISH_TO_CW = 44,
    ASSISTED_TARGET_DETECTION = 47,
}

export interface JobDatatype {
    type?: string;
    ext?: string;
    format?: string;
    oriCRS?: string;
    destCRS?: string;
    category?: Category;
    categoryString?: string;
    isFinal?: boolean;
}

export interface PcvizJobConfig {
    files?: File[] | string[];
    fileSizes?: number[];
    jobName?: string;
    catalogId?: string | null;
    project?: PcvizProject;
    crs?: any;
    zipFolder?: string;
    zipName?: string;
}

export interface PcvizJobProcessData {
    jobName: string;
    scans: string[];
    outputData: any;
    options: Partial<PcvizScanProcessingOptions>;
}

export declare class PcvizJob {
    constructor(job?: Job);
    getPipeline(): Pipeline;
    getJobId(): number;
    getProcessId(): number;
    setProcessId(processId: number): void;
    getName(): string;
    setName(name: string): void;
    getInput(): string;
    setInput(input: string): void;
    getOutput(): string;
    setOutput(output: string): void;
    getInputData(): string;
    setInputData(inputData: string): void;
    getOutputData(): string;
    setOutputData(outputData: string): void;
    create(createAndStartOption: boolean): Promise<Job>;
    start(): Promise<unknown>;
    cancel(): Promise<unknown>;
    estimate(): Promise<PcvizEstimationCost>;
    on(action: string, funct: (...args: any[]) => void): unknown;
    emit(action: string, ...args: any[]): void;
    getDriver(): unknown;
    startCheckStatus(): void;
}

export type PcvizJobConstructor = {
    new (job?: Job): PcvizJob;
};

export declare class PcvizJobAdapter {
    _id: string | undefined;
    _projectName: string;
    _projectType: ProjectType;
    _processingOptions: any;
    _formatId: string;
    layerType: string | undefined;
    acceptsPatterns: { txt: string };
    mandatoryDataFileExtension: string[];
    options: any[] | Record<string, any>;
    remoteProcessId: Process;
    category: Category | undefined;
    categoryString: string;
    categoryList: Category[];
    crs: string;
    destCrs: string;
    formatType: string;
    isFinal: boolean;
    detectedType: string;
    extensions: string[];
    enabled: boolean;

    getId(): string;
    setId(value?: string): void;
    getExtension(fileName: string): boolean;
    getExtensions(): string[];
    isSetByUser(): boolean;
    isEnabled(): boolean;
    getOptions(): any[] | Record<string, any>;
    setEnabled(value: boolean): void;
    getFormatDefinitions(): any;
    isFileValid(file: File, extensions?: string[]): boolean;
    acceptFile(extension: string): string | undefined;
    canUseExtraPanels(): boolean;
    detect(files: File[]): Promise<JobDatatype[]>;
    getServerCategoryByName(name: string): Category | undefined;
    setServerCategoryList(categoryList: Category[]): void;
    areLayerFilesValid(files: File[]): boolean;
    getLimitTotalSize(): number;
    getMaximumFileSize(): number;
    getMaximumFileCount(): number;
    setCRS(value?: string): void;
    setDestCRS(value?: string): void;
    setCategory(value?: Category): void;
    getCategory(): Category;
    setCategoryString(value?: string): void;
    getCategoryString(): string;
    setFormatType(value?: string): void;
    setIsFinal(value?: boolean): void;
    setImporterOptions(importerOptions: ImporterConverterOptions): void;
    getImporterOptions(): ImporterConverterOptions;
    getDetectedType(): string;
    getFormatType(): string;
    getLayerType(): LayerType;
    getObjectType(): string;
    generateValidName(name: string): string;
    getCompleteJob(config: PcvizJobConfig): PcvizJob;
    setOptions(value: any): void;
    setProjectVersion(version: number): void;
    setFilesArray(filesArray: string[]): void;
    enableWorkspaceProject(value?: boolean): void;
    setJobType(name: string): void;
    setExtraInputData(value?: any): void;
    setExtraOutputData(value?: any): void;
    setConverterOptions(value: any): void;
    getConverterOptions(): any;
    setObjectType(objectType: string): void;
    setWorkspaceImportPath(path: string): void;
    computeName(config: PcvizJobConfig, translateService: BaseTranslationService): void;
    setByUser(value: boolean): void;
    overrideExtensions(extensions: string[]): void;
}

export type PcvizJobAdapterConstructor = {
    new (): PcvizJobAdapter;
};

export interface PcvizEstimationCost {
    concurrentInstances: number;
    id: number;
    processingCredits: number;
    processingTime: number;
    processingUnits: number;
    unitsToCredits: number;
}

export type PcivzDeleteDataJobAdapter = PcvizJobAdapter;
export type PcivzDeleteDataJobAdapterConstructor = PcvizJobAdapterConstructor;

export interface ImporterConverterOptions {
    SaveBlocksExchange?: number;
    OnlyFirstMetadataImages?: number;
    ScaleFactor?: number;
    YAxisUp?: number;
    GeometryType?: any;
    CSVHeaders?: string;
}

export interface GisJobAdapter extends PcvizJobAdapter {
    _csvOptions: ScanControlPointType;
    getCsvOptions(): ScanControlPointType;
}

export interface PcvizJobAdapterService {
    ensureJobAdapter(jobAdapter: PcvizJobAdapter): PcvizJobAdapter;
    getJobAdapter(): PcvizJobAdapter;
    setJobAdapter(jobAdapter?: PcvizJobAdapter): void;
    clearService(): void;
}

export interface PcvizJobAdapterFormatDefinition {
    uploaderName?: string;
    id?: string;
    label?: string;
    fileType?: any;
}

export interface PcvizJobAdapterRegistry extends PcvizDBRegistry {
    importers: any;
    currentJob: PcvizJobAdapter;

    getInstance(): PcvizJobAdapterRegistry;
    setEnabledAllJobAdapter(value: boolean): void;
    setEnabledJobAdapter(id: string, enabled: boolean): void;
    getJobAdapterByExt(extension: string): { uploaders: PcvizJobAdapter[]; priority: PcvizJobAdapter[] };
    getImporter(importer?: string): PcvizJobAdapter;
    getAcceptedFileExtensions(): string;
}

export declare class PcvizImportZipProjectJobAdapter {
    getExtensions(): string[];
}

export type PcvizImportZipProjectJobAdapterConstructor = {
    new (): PcvizImportZipProjectJobAdapter;
};

export interface PcvizExportProjectJobAdapter {
    getExportFileName(config: JobConfiguration): string;
    setObjectName(value: string | undefined): void;
    setProjectType(projectTyype: ProjectType): void;
    getCompleteJob(configuration: JobConfiguration): any;
}

export type PcvizExportProjectJobAdapterConstructor = {
    new (): PcvizExportProjectJobAdapter;
};

export interface PcvizUploadUtils {
    addLayersToProjectConfig: () => void;
    checkFilesToUpload: (config?: PcvizUploadConfig) => void;
    convertLayer: (config: PcvizUploadConfig) => void;
    uploadFiles: (config: PcvizUploadConfig) => void;
    createAndUpload: (config?: PcvizUploadConfig) => Promise<number>;
    createLayer: () => void;
    createUploadJob: (config: PcvizUploadConfig) => Promise<number>;
    uploadFilesByConfig: (config: PcvizUploadConfig) => Promise<void>;
    ensureValidName: () => void;
    getFileSize: (file: File) => number;
    getOrCreateLayers: () => void;
    getUploadFilesList: () => void;
    prepareAndGetOrCreateLayers: (project: any, config: PcvizUploadConfig) => Promise<void>;
    prepareFilesInfo: () => void;
    prepareProcessConfig: () => void;
    prepareUploadFilesListForEstimation: () => void;
    process: () => void;
    processLayer: () => void;
    uploadJobSiteReferenceData: (config: PcvizUploadConfig) => void;
}

export interface PcvizUploadUrlInfo {
    filePath: string;
    size: number | undefined;
}

export interface PcvizUploadConfigData {
    id?: number | string;
    isIncomplete?: boolean;
    status?: JobStatus;
    sessionId?: number;
    uploadJob?: boolean;
    uploadId?: number | string;
    project?: any;
    progress?: number;
    filesInfo?: PcvizUploadConfigFileInfo[];
    createdAt?: string;
    jobAdapter?: PcvizJobAdapter;
    startedAt?: number;
    remainingTime?: number;
    useFilePath?: boolean;
    catalogObjects?: any;
    output?: any;
    prefix?: any;
    updatedAt?: number;
    object?: PcvizUploadConfigObject;
    urls?: PcvizUploadUrlInfo[];
    cancel?: () => void;
    pause?: () => void;
    resume?: () => void;
    setStatus?: (status: JobStatus) => void;
}

export interface PcvizUploadConfigFileInfo {
    invalid?: boolean;
    invalidMessage?: string;
    completed?: boolean;
    loaded?: number;
    mustUpload?: boolean;
    name?: string;
    progress?: number;
    isIndeterminate?: boolean;
    progressParts?: number[];
    size?: number;
    totalParts?: number;
}

export interface PcvizUploadConfigObject {
    accessLevel?: string;
    algorithm?: string;
    category?: any;
    configuration?: any;
    createdAt?: string;
    createdBy?: number;
    createdByCompany?: string;
    creatorUserId?: string;
    dataDriver?: string;
    dataOrigin?: any;
    description?: string;
    features?: any;
    geoid?: string;
    horizProjectionEPSG?: string;
    id?: number;
    incomplete?: boolean;
    layerType?: string;
    links?: Array<{ rel: string; href: string }>;
    majorVersion?: string;
    maxBlue?: number;
    maxClass?: number;
    maxGreen?: number;
    maxIntensity?: number;
    maxRed?: number;
    maxx?: number;
    maxy?: number;
    maxz?: number;
    minBlue?: number;
    minClass?: number;
    minGreen?: number;
    minIntensity?: number;
    minRed?: number;
    minVersion?: string;
    minx?: number;
    miny?: number;
    minz?: number;
    name?: string;
    ownerCompanyId?: string;
    patchVersion?: string;
    points?: number;
    projectsCount?: number;
    projectType?: ProjectType;
    shared?: boolean;
    size?: number;
    storageUrl?: any;
    thumbnail?: any;
    type?: string;
    uid?: string;
    updatedAt?: string;
    url?: any;
    captureDate?: string;
}

export interface PcvizUploadConfigProject {
    project_data: {
        name?: string;
        description?: string;
        captureDate?: string;
    };
}

export interface PcvizUploadConfigWorkspace {
    name: string;
}

export interface PcvizUploadConfig {
    ended?: string;
    id?: string;
    files: File[];
    upload?: PcvizUploadConfigData;
    isProgressBarHidden?: boolean;
    catalogObjects?: any;
    output?: any;
    prefix?: any;
    uploadId?: string | number;
    crs?: any;
    isVisibleCrs?: boolean;
    project?: PcvizUploadConfigProject;
    formatType?: { type: string | undefined };
    object?: PcvizUploadConfigObject;
    uploadTime?: number;
    jobAdapter?: PcvizJobAdapter;
    outputUUID?: string;
    name?: string;
    filesPath?: string;
    workspace?: PcvizUploadConfigWorkspace;
    resume?: boolean;
}

export interface ControllerDBUpload {
    isCurrentlyUploading(): boolean;
    getCurrentUploadingSessionId(): string;
    updateInLocalStorage(value: PcvizUploadConfigData): void;
    removeUploadJob(uploadJob: any): void;
    getJob(id: number): Job;
}

export type PcvizCreateStationJobAdapter = PcvizJobAdapter;

export interface PcvizZipJobAdapter {
    ZIP_TEMP_PATH: string;
    addExcludedPath(path: string): void;
    getCompleteJob(config: any): PcvizJob;
    getZipFileName(config: any): string;
    enableWorkspaceProject(): void;
}

export type PcVizZipJobAdapterConstructor = {
    new (): PcvizZipJobAdapter;
};

export interface PcvizScanProjectJobAdapter extends PcvizJobAdapter {
    ID: string;
    setOuputFolder(folderName: string): void;
    setOutputFormats(formats: string[]): void;
    setFormatOptions(options: Record<string, boolean>): void;
    getUnselectedElements(): unknown[];
    setUnselectedElements(elements: unknown[]): void;
    setScans(scans: PcvizWorkspaceScan[]): void;
    getScans(): PcvizWorkspaceScan[];
    getOptions(): PcvizScanProcessingOptions;
}

export interface PcvizCRP1ScanProjectJobAdapter extends PcvizJobAdapter {
    new (): PcvizCRP1ScanProjectJobAdapter;
    setScans(scans: PcvizCRP1ScanData[]): void;
    getScans(): PcvizCRP1ScanData[] | undefined;
    getScansData(): PcvizCRP1ImportJobScanData[];
    getDstSrcFilePathsFor(projectId: number): PcvizCRP1DstSrcFilePaths;
    getOptions(): PcvizScanProcessingOptions;
    setDataPath(dataPath: string): void;
    ID: string;
    CRP1_IMPORT_ID: string;
}

export interface PcvizCRP1DstSrcFilePaths {
    source: string;
    dest: string;
}
export interface PcvizCRP1FileData {
    content: { projects: PcvizCRP1Project[] };
    path: string;
    size: string;
    success: boolean;
}
export interface PcvizCRP1ScanData {
    name: string;
    captureDate: string;
    filesPath: string;
    capturedWithCamera: boolean;
    scannerType: string;
}

export interface PcvizCRP1ImportJobScanData {
    name: string;
    source: string;
    hasPanorama: boolean;
}

export interface PcvizCRP1Project {
    name: string;
    scans: PcvizCRP1ScanData[];
}

export type PcvizScanProjectJobAdapterConstructor = {
    new (): PcvizScanProjectJobAdapter;
};

export type PcvizCRP1ScanProjectJobAdapterConstructor = {
    new (): PcvizCRP1ScanProjectJobAdapter;
};

export interface PcvizJobs {
    Job: PcvizJobConstructor;
    JobAdapterService: PcvizJobAdapterService;
    JobAdapterRegistry: PcvizJobAdapterRegistry;
    ImportZipProjectJobAdapter: PcvizImportZipProjectJobAdapterConstructor;
    ExportProjectJobAdapter: PcvizExportProjectJobAdapterConstructor;
    UploadUtils: PcvizUploadUtils;
    DeleteDataJobAdapter: PcivzDeleteDataJobAdapterConstructor;
    ZipJobAdapter: PcVizZipJobAdapterConstructor | PcvizZipJobAdapter;
    ScanProjectJobAdapter: PcvizScanProjectJobAdapter | PcvizScanProjectJobAdapterConstructor;
    CRP1ScanProjectJobAdapter: PcvizCRP1ScanProjectJobAdapter;
    Process: PcvizProcess;
}

export interface PcvizCompanyReport {
    company: {
        id: string;
        name: string;
        processingCost: number;
        storageSize: number;
        transferSize: number;
    };
    licences: {
        maxprocessing: number;
        maxstorage: number;
        maxtransfer: number;
    };
}

export interface PcvizEstimationCostCredits {
    availableCredits?: number;
    estimation?: PcvizEstimationCost;
    creditsAfterProcessing?: number;
}

export const DESIGN_DATA_JOB_TYPE = 'DduVectorLayer';
export const GCP_JOB_TYPE = 'GcpVectorLayer';
